import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Star, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

/**
 * ReviewModal Component
 * Two-way review system for appointments (Airbnb-inspired)
 * - Clients review specialists
 * - Specialists review clients
 */
export const ReviewModal = ({ 
  isOpen, 
  onClose, 
  appointment,
  onSubmitReview 
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  
  // Review form state
  const [starRating, setStarRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [textReview, setTextReview] = useState('');
  const [attributes, setAttributes] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Determine if current user is reviewing as specialist or client
  const [reviewerRole, setReviewerRole] = useState(null); // 'specialist' or 'client'
  const [revieweeId, setRevieweeId] = useState(null);
  const [revieweeName, setRevieweeName] = useState('');
  
  useEffect(() => {
    if (appointment && user) {
      console.log('🔍 [REVIEW MODAL] Appointment data:', appointment);
      console.log('🔍 [REVIEW MODAL] Current user:', user);
      
      let revieweeName = '';
      let revieweeId = null;
      let userIsSpecialist = false;
      
      // Determine roles based on appointment data structure
      // Support both naming conventions: provider/requester AND specialist/client
      const providerName = appointment.provider_name || appointment.specialist_name;
      const providerId = appointment.provider_id || appointment.specialist_id;
      const requesterName = appointment.requester_name || appointment.client_name;
      const requesterId = appointment.requester_id || appointment.client_id;
      
      // Am I the provider in this booking?
      userIsSpecialist = (providerId === user.id);
      
      console.log('🔍 [REVIEW MODAL] User is provider?', userIsSpecialist);
      console.log('🔍 [REVIEW MODAL] Provider ID:', providerId, 'User ID:', user.id);
      console.log('🔍 [REVIEW MODAL] Requester ID:', requesterId);
      
      // Determine who is being reviewed
      if (userIsSpecialist) {
        // I'm the provider, reviewing the client/requester
        revieweeName = requesterName || t('thisUser');
        revieweeId = requesterId;
        console.log('✅ [REVIEW MODAL] Reviewing REQUESTER:', revieweeName, 'ID:', revieweeId);
      } else {
        // I'm the client, reviewing the provider/specialist
        revieweeName = providerName || t('thisUser');
        revieweeId = providerId;
        console.log('✅ [REVIEW MODAL] Reviewing PROVIDER:', revieweeName, 'ID:', revieweeId);
      }
      
      // Universal attributes for everyone (removed payment_prompt, kept universal traits)
      setAttributes({
        punctual: false,
        professional: false,
        respectful: false,
        communicative: false,
        friendly: false
      });
      
      setReviewerRole(userIsSpecialist ? 'specialist' : 'client');
      setRevieweeId(revieweeId);
      setRevieweeName(revieweeName);
    }
  }, [appointment, user]);
  
  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStarRating(0);
      setHoveredStar(0);
      setTextReview('');
      setAttributes({});
    }
  }, [isOpen]);
  
  // Toggle attribute checkbox
  const toggleAttribute = (key) => {
    setAttributes(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (starRating === 0) {
      alert('Please select a star rating');
      return;
    }
    
    // Validate reviewee_id exists
    if (!revieweeId) {
      console.error('❌ [REVIEW MODAL] No reviewee_id found');
      alert('Cannot submit review: Missing reviewer information. Please ensure the appointment has proper specialist/client IDs.');
      return;
    }
    
    setIsSubmitting(true);
    
    const reviewData = {
      appointment_id: String(appointment.id), // Convert to string for backend
      reviewee_id: String(revieweeId), // Ensure this is also a string
      role: reviewerRole === 'client' ? 'specialist' : 'client', // Role of reviewee
      star_rating: starRating,
      attributes,
      text_review: textReview.trim() || null
    };
    
    console.log('📝 [REVIEW MODAL] Submitting review:', reviewData);
    console.log('📝 [REVIEW MODAL] Reviewee ID:', revieweeId);
    console.log('📝 [REVIEW MODAL] Reviewer role:', reviewerRole);
    
    // Call parent handler
    if (onSubmitReview) {
      await onSubmitReview(reviewData);
    }
    
    setIsSubmitting(false);
    onClose();
  };
  
  // Universal attribute labels (same for all users)
 const getAttributeLabel = (key) => {
  const labels = {
    punctual: t('onTimePunctual'),
    professional: t('professional'),
    respectful: t('respectful'),
    communicative: t('goodCommunication'),
    friendly: t('friendlyPleasant')
  };
  return labels[key] || key;
};
  
  if (!appointment || !reviewerRole) {
    return null;
  }
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            {t('leaveAReview')}
          </DialogTitle>
          <DialogDescription>
            {t('shareYourExperience')} <span className="font-semibold">{revieweeName}</span>
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          {/* Star Rating */}
          <div>
            <Label className="text-base font-semibold mb-3 block">
              {t('overallRating')} <span className="text-red-500">{t('required')}</span>
            </Label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setStarRating(star)}
                  onMouseEnter={() => setHoveredStar(star)}
                  onMouseLeave={() => setHoveredStar(0)}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    className={`w-10 h-10 ${
                      star <= (hoveredStar || starRating)
                        ? 'fill-amber-400 text-amber-400'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
              {starRating > 0 && (
                <span className="ml-2 text-lg font-semibold text-amber-600">
                  {starRating === 1 ? t('star') : t('stars')}
                </span>
              )}
            </div>
          </div>
          
          {/* Attribute Checkboxes */}
          <div>
            <Label className="text-base font-semibold mb-3 block">
              {t('howWas')} {revieweeName}?
            </Label>
            <div className="grid grid-cols-2 gap-3">
              {Object.keys(attributes).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleAttribute(key)}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                    attributes[key]
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div
                    className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                      attributes[key]
                        ? 'bg-green-500 border-green-500'
                        : 'border-gray-300'
                    }`}
                  >
                    {attributes[key] && <Check className="w-4 h-4 text-white" />}
                  </div>
                  <span className="text-sm font-medium">
                    {getAttributeLabel(key)}
                  </span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Optional Text Review */}
          <div>
            <Label className="text-base font-semibold mb-3 block">
              {t('additionalComments')}
            </Label>
            <Textarea
              value={textReview}
              onChange={(e) => setTextReview(e.target.value)}
              placeholder={t('shareMoreDetails')}
              rows={4}
              className="resize-none"
              maxLength={500}
            />
            <p className="text-xs text-gray-500 mt-1">
              {textReview.length}/500 {t('characters')}
            </p>
          </div>
          
          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>{t('note')}:</strong> {t('reviewVisibilityNote', { name: revieweeName })}
            </p>
          </div>
          
          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || starRating === 0}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {isSubmitting ? t('submitting') : t('submitReview')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
